// Arrows and fishing-rod bobbers. The server flies them for real (hits, sticking,
// pickups); clients fly the same path locally between its updates so they move smoothly.

export type ProjKind = "arrow" | "bobber";

/** Blocks/second off a fully drawn bow. Minecraft's is 60; a touch slower so you can see it fly. */
export const ARROW_SPEED = 48;
/** Seconds: shorter draws don't fire, and a draw tops out here. */
export const BOW_MIN_DRAW = 0.2;
export const BOW_FULL_DRAW = 1;
export const BOBBER_SPEED = 17;
/** Past this the line snaps and the bobber comes home. */
export const ROD_RANGE = 16;
/** Stuck arrows disappear after this long. */
export const ARROW_LIFE = 30_000;

// Gravity in blocks/s², drag as Minecraft's per-tick multiplier.
const PHYS: Record<ProjKind, { g: number; drag: number; waterDrag: number }> = {
  arrow: { g: 20, drag: 0.99, waterDrag: 0.6 },
  bobber: { g: 18, drag: 0.97, waterDrag: 0.5 },
};
/** Longest hop between collision checks, so fast arrows can't skip through a block or a player. */
const SUBSTEP = 0.2;

export interface Flying {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
}

export interface ProjWorld {
  solid(x: number, y: number, z: number): boolean;
  water(x: number, y: number, z: number): boolean;
  /** Leaving the map: arrows just vanish rather than sticking in the invisible wall. */
  outside(x: number, y: number, z: number): boolean;
}

/** Minecraft's bow curve: power 0..1 from how long the string was held back. */
export function bowPower(drawSecs: number): number {
  const c = Math.max(0, Math.min(1, drawSecs / BOW_FULL_DRAW));
  return (c * c + 2 * c) / 3;
}

/**
 * Arrow damage before armor, from draw time: 2 for the quickest shot up to 6,
 * and a full draw is a crit (x1.5, so 9). Null if it was too short to fire.
 */
export function arrowDamage(drawSecs: number): { dmg: number; crit: boolean } | null {
  if (!(drawSecs >= BOW_MIN_DRAW)) return null;
  const k = Math.min(1, (drawSecs - BOW_MIN_DRAW) / (BOW_FULL_DRAW - BOW_MIN_DRAW));
  const crit = drawSecs >= BOW_FULL_DRAW;
  const dmg = 2 + 4 * k;
  return { dmg: crit ? dmg * 1.5 : dmg, crit };
}

/** Launch velocity along a yaw/pitch (the same convention as the camera). */
export function launch(yaw: number, pitch: number, speed: number): [number, number, number] {
  const cp = Math.cos(pitch);
  return [-Math.sin(yaw) * cp * speed, Math.sin(pitch) * speed, -Math.cos(yaw) * cp * speed];
}

/**
 * Flies a projectile for dt seconds. Stops at the first thing it runs into:
 * "block" (left just outside it), "water", "out" of the map, or "hit" when
 * `hits` says a point is inside someone.
 */
export function travel(p: Flying, kind: ProjKind, dt: number, w: ProjWorld, hits?: (x: number, y: number, z: number) => boolean): "block" | "water" | "out" | "hit" | null {
  const ph = PHYS[kind];
  const wet = w.water(Math.floor(p.x), Math.floor(p.y), Math.floor(p.z));
  const drag = Math.pow(wet ? ph.waterDrag : ph.drag, dt * 20);
  p.vx *= drag;
  p.vy = p.vy * drag - ph.g * dt;
  p.vz *= drag;
  const dist = Math.hypot(p.vx, p.vy, p.vz) * dt;
  const n = Math.max(1, Math.ceil(dist / SUBSTEP));
  const h = dt / n;
  for (let i = 0; i < n; i++) {
    const x = p.x + p.vx * h, y = p.y + p.vy * h, z = p.z + p.vz * h;
    if (w.outside(x, y, z)) return "out";
    if (w.solid(Math.floor(x), Math.floor(y), Math.floor(z))) {
      // A bobber that hits a wall drops straight down it instead of sticking there.
      if (kind === "bobber" && (p.vx || p.vz) && !w.solid(Math.floor(p.x), Math.floor(y), Math.floor(p.z))) {
        p.vx = p.vz = 0;
        continue;
      }
      return "block";
    }
    p.x = x;
    p.y = y;
    p.z = z;
    if (hits?.(x, y, z)) return "hit";
    if (kind === "bobber" && w.water(Math.floor(x), Math.floor(y), Math.floor(z))) return "water";
  }
  return null;
}
